// Producer (video script boards) API client. Talks to studio-api /api/producer/*,
// which proxies to the content engine. Every mutation returns the persisted record;
// the UI renders only what the server confirmed.

import { apiBase } from './api';

export type VideoPlatform = 'youtube' | 'youtube_shorts' | 'tiktok' | 'instagram' | 'facebook';
export type NodeKind = 'video' | 'note' | 'chat' | 'script';
export type ScriptFormat = 'youtube_long' | 'youtube_shorts' | 'reels' | 'tiktok';

export const FORMAT_LABELS: Record<ScriptFormat, string> = {
  youtube_long: 'YouTube long-form',
  youtube_shorts: 'YouTube Shorts',
  reels: 'Instagram Reels',
  tiktok: 'TikTok',
};

export const PLATFORM_LABELS: Record<VideoPlatform, string> = {
  youtube: 'YouTube',
  youtube_shorts: 'YouTube Shorts',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

export interface Board {
  id: string;
  title: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  node_count?: number;
}

export interface VideoData {
  platform: VideoPlatform;
  url: string;
  title?: string;
  author?: string;
  thumbnail_url?: string;
  status: 'pending' | 'transcribing' | 'ready' | 'failed';
  error?: string | null;
  excerpt?: string;
  words?: number;
  duration_sec?: number | null;
}
export interface NoteData { text: string }
export interface ChatMessage { role: 'user' | 'assistant'; text: string; at: string; by?: string; source_count?: number }
export interface ChatData { title: string; messages: ChatMessage[] }
export interface ScriptBeat { time: string; voiceover: string; on_screen?: string; b_roll?: string }
export interface ScriptData {
  format: ScriptFormat;
  title: string;
  hook: string;
  beats: ScriptBeat[];
  cta: string;
  length_sec?: number;
  brief?: string;
}

export interface BoardNode {
  id: string;
  kind: NodeKind;
  x: number;
  y: number;
  content_item_id: string | null;
  data: VideoData | NoteData | ChatData | ScriptData;
  created_by: string;
  updated_at: string;
}
export interface BoardEdge { id: string; source_node_id: string; target_node_id: string }

export interface ProducerMe { authenticated: boolean; email: string; operator: boolean }

const BASE = `${apiBase()}/api/producer`;

/** Error carrying the server's own message so the UI can show it verbatim. */
export class ProducerError extends Error {
  constructor(message: string, public status: number, public body?: { node?: BoardNode }) {
    super(message);
  }
}

async function call<T>(path: string, method: 'GET' | 'POST' | 'PATCH' | 'DELETE' = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    window.location.href = '/operator/login';
    throw new ProducerError('Signing you in again.', 401);
  }
  let json: any = null;
  try { json = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    throw new ProducerError(json?.error ?? `Request failed (${res.status}).`, res.status, json ?? undefined);
  }
  return json as T;
}

/** Non-redirecting identity probe for the nav + route guard. */
export async function producerMe(): Promise<ProducerMe | null> {
  try {
    const res = await fetch(`${apiBase()}/auth/producer/me`, { credentials: 'include' });
    if (!res.ok) return null;
    return (await res.json()) as ProducerMe;
  } catch {
    return null;
  }
}

export async function staffSignOut(): Promise<void> {
  await fetch(`${apiBase()}/auth/operator/signout`, { method: 'POST', credentials: 'include' }).catch(() => {});
}

export const producerApi = {
  listBoards: () => call<{ boards: Board[] }>('/boards'),
  createBoard: (title: string) => call<{ board: Board }>('/boards', 'POST', { title }),
  getBoard: (id: string) => call<{ board: Board; nodes: BoardNode[]; edges: BoardEdge[] }>(`/boards/${id}`),
  renameBoard: (id: string, title: string) => call<{ ok: true; title: string }>(`/boards/${id}`, 'PATCH', { title }),
  deleteBoard: (id: string) => call<{ ok: true }>(`/boards/${id}`, 'DELETE'),

  addNode: (boardId: string, input: { kind: 'video' | 'note' | 'chat'; x: number; y: number; url?: string; text?: string; title?: string }) =>
    call<{ node: BoardNode }>(`/boards/${boardId}/nodes`, 'POST', input),
  moveNode: (boardId: string, nodeId: string, x: number, y: number) =>
    call<{ node: BoardNode }>(`/boards/${boardId}/nodes/${nodeId}`, 'PATCH', { x, y }),
  editNode: (boardId: string, nodeId: string, patch: { text?: string; title?: string }) =>
    call<{ node: BoardNode }>(`/boards/${boardId}/nodes/${nodeId}`, 'PATCH', patch),
  deleteNode: (boardId: string, nodeId: string) => call<{ ok: true }>(`/boards/${boardId}/nodes/${nodeId}`, 'DELETE'),

  transcript: (boardId: string, nodeId: string) => call<{ transcript: string }>(`/boards/${boardId}/nodes/${nodeId}/transcript`),
  transcribe: (boardId: string, nodeId: string) => call<{ node: BoardNode }>(`/boards/${boardId}/nodes/${nodeId}/transcribe`, 'POST'),
  chat: (boardId: string, nodeId: string, message: string) =>
    call<{ node: BoardNode; reply: string; source_count: number }>(`/boards/${boardId}/nodes/${nodeId}/chat`, 'POST', { message }),
  writeScript: (boardId: string, nodeId: string, format: ScriptFormat, brief: string) =>
    call<{ node: BoardNode; edge: BoardEdge | null }>(`/boards/${boardId}/nodes/${nodeId}/script`, 'POST', { format, brief }),

  connect: (boardId: string, source: string, target: string) =>
    call<{ edge: BoardEdge }>(`/boards/${boardId}/edges`, 'POST', { source, target }),
  disconnect: (boardId: string, edgeId: string) => call<{ ok: true }>(`/boards/${boardId}/edges/${edgeId}`, 'DELETE'),
};

export function scriptAsText(s: ScriptData): string {
  const beats = s.beats
    .map(b => `[${b.time}]\nPICTURE: ${[b.b_roll, b.on_screen ? `On screen: ${b.on_screen}` : ''].filter(Boolean).join(' / ') || '(talking head)'}\nSOUND: ${b.voiceover}`)
    .join('\n\n');
  return `${s.title}\n${FORMAT_LABELS[s.format]}\n\nHOOK: ${s.hook}\n\n${beats}\n\nCTA: ${s.cta}\n`;
}
